import React, { useState, useEffect, useRef, PropsWithChildren } from 'react'
import { RtcPropsInterface, mediaStore } from './PropsContext'
import {
  ILocalVideoTrack,
  ILocalAudioTrack,
  createMicrophoneAndCameraTracks
} from 'agora-rtc-react'
import AgoraRTC from 'agora-rtc-react'
import { TracksProvider } from './TracksContext'

const useTracks = createMicrophoneAndCameraTracks(
  { encoderConfig: {} },
  { encoderConfig: {} }
)

/**
 * Track state status indicating the current initialization state of a media track.
 *
 * @remarks
 * - `loading`: Track initialization is in progress
 * - `ready`: Track has been successfully created and is available for use
 * - `failed`: Track initialization failed due to an error
 * - `unavailable`: Track is not available (e.g., device not found)
 */
export type TrackStatus = 'loading' | 'ready' | 'failed' | 'unavailable'

/**
 * Track error types categorizing different failure scenarios during track initialization.
 *
 * @remarks
 * - `permission_denied`: User denied browser permission to access the device
 * - `device_unavailable`: Device not found or not connected
 * - `initialization_failed`: Track creation failed for other reasons
 */
export type TrackErrorType =
  | 'permission_denied'
  | 'device_unavailable'
  | 'initialization_failed'

/**
 * Track error information providing details about track initialization failures.
 *
 * @property type - The category of error that occurred
 * @property message - Human-readable error message with context
 * @property canRetry - Whether retrying track initialization might succeed
 *
 * @example
 * ```typescript
 * const error: TrackError = {
 *   type: 'permission_denied',
 *   message: 'Camera access denied. Please allow camera access in your browser settings.',
 *   canRetry: true
 * }
 * ```
 */
export interface TrackError {
  type: TrackErrorType
  message: string
  canRetry: boolean
}

/**
 * Track state information containing the current status, track instance, and error details.
 *
 * @property status - Current initialization status of the track
 * @property track - The Agora track instance (null if not yet created or failed)
 * @property error - Error information if track initialization failed (optional)
 *
 * @remarks
 * This interface is used internally by TracksConfigure to manage track state.
 * For external consumption, use {@link TrackAvailability} from TracksContext instead.
 *
 * @example
 * ```typescript
 * const audioState: TrackState = {
 *   status: 'ready',
 *   track: audioTrackInstance,
 *   error: undefined
 * }
 *
 * const videoState: TrackState = {
 *   status: 'failed',
 *   track: null,
 *   error: {
 *     type: 'permission_denied',
 *     message: 'Camera access denied',
 *     canRetry: true
 *   }
 * }
 * ```
 */
export interface TrackState {
  status: TrackStatus
  track: ILocalAudioTrack | ILocalVideoTrack | null
  error?: TrackError
}

/**
 * Error message templates for different failure scenarios
 */
const ERROR_MESSAGES: Record<
  string,
  { message: string; guidance: string; canRetry: boolean }
> = {
  camera_permission_denied: {
    message:
      'Camera access denied. Please allow camera access in your browser settings to enable video.',
    guidance:
      "Click the camera icon in your browser's address bar and select 'Allow'.",
    canRetry: true
  },
  camera_unavailable: {
    message: 'No camera detected. You can still join with audio only.',
    guidance: 'Connect a camera device to enable video.',
    canRetry: true
  },
  microphone_permission_denied: {
    message:
      'Microphone access denied. Please allow microphone access to join the call.',
    guidance:
      "Click the microphone icon in your browser's address bar and select 'Allow'.",
    canRetry: true
  },
  microphone_unavailable: {
    message:
      'No microphone detected. A microphone is required to join the call.',
    guidance: 'Connect a microphone device to join.',
    canRetry: true
  },
  both_denied: {
    message:
      'Camera and microphone access denied. Please allow access in your browser settings.',
    guidance:
      'Refresh the page and allow access when prompted, or check your browser settings.',
    canRetry: true
  },
  initialization_failed: {
    message: 'Failed to initialize media device.',
    guidance: 'Please try again or check your device connections.',
    canRetry: true
  }
}

/**
 * Classify error by type based on error message or code
 */
function classifyError(error: any, trackType: 'audio' | 'video'): TrackError {
  const errorMessage = error?.message?.toLowerCase() || ''
  const errorName = error?.name?.toLowerCase() || ''

  // Check for permission denied errors
  if (
    errorMessage.includes('permission') ||
    errorMessage.includes('denied') ||
    errorName.includes('notallowederror') ||
    errorName.includes('permissiondeniederror')
  ) {
    const template =
      trackType === 'video'
        ? ERROR_MESSAGES.camera_permission_denied
        : ERROR_MESSAGES.microphone_permission_denied
    return {
      type: 'permission_denied',
      message: template.message,
      canRetry: template.canRetry
    }
  }

  // Check for device unavailable errors
  if (
    errorMessage.includes('not found') ||
    errorMessage.includes('no device') ||
    errorMessage.includes('could not start') ||
    errorName.includes('notfounderror') ||
    errorName.includes('devicenotfounderror')
  ) {
    const template =
      trackType === 'video'
        ? ERROR_MESSAGES.camera_unavailable
        : ERROR_MESSAGES.microphone_unavailable
    return {
      type: 'device_unavailable',
      message: template.message,
      canRetry: template.canRetry
    }
  }

  // Default to initialization failed
  const template = ERROR_MESSAGES.initialization_failed
  return {
    type: 'initialization_failed',
    message: `${template.message} ${errorMessage || 'Unknown error'}`,
    canRetry: template.canRetry
  }
}
/**
 * React component that create local camera and microphone tracks and assigns them to the child components
 */
const TracksConfigure: React.FC<
  PropsWithChildren<Partial<RtcPropsInterface>>
> = (props) => {
  const [ready, setReady] = useState<boolean>(false)
  const [audioTrackState, setAudioTrackState] = useState<TrackState>({
    status: 'loading',
    track: null
  })
  const [videoTrackState, setVideoTrackState] = useState<TrackState>({
    status: 'loading',
    track: null
  })
  const { ready: trackReady, tracks, error } = useTracks()
  const mediaStore = useRef<mediaStore>({})
  // Tracks created here (outside the combined hook) — closed on unmount.
  const separateTracksRef = useRef<Array<ILocalAudioTrack | ILocalVideoTrack>>(
    []
  )
  const unmountedRef = useRef<boolean>(false)

  const createCameraTrack = async (): Promise<ILocalVideoTrack> => {
    const track = await AgoraRTC.createCameraVideoTrack({ encoderConfig: {} })
    separateTracksRef.current.push(track)
    return track
  }

  const createMicrophoneTrack = async (): Promise<ILocalAudioTrack> => {
    const track = await AgoraRTC.createMicrophoneAudioTrack({
      encoderConfig: {}
    })
    separateTracksRef.current.push(track)
    return track
  }

  /**
   * Retry whichever device failed (camera and/or microphone) WITHOUT tearing the call down.
   *
   * The previous implementation set `ready` back to false, which unmounted the whole call UI and
   * left the channel — the other participant then received `user-left` and the call ended. A
   * retry now only creates the missing track(s) and swaps them in; RtcConfigure publishes a newly
   * available track on its own.
   */
  const retryTrackInitialization = async (): Promise<void> => {
    console.log('[TracksConfigure] Retrying missing media devices...')
    let latestAudio = audioTrackState.track as ILocalAudioTrack | null
    let latestVideo = videoTrackState.track as ILocalVideoTrack | null
    const syncMediaStore = () => {
      mediaStore.current[0] = {
        ...(latestAudio ? { audioTrack: latestAudio } : {}),
        ...(latestVideo ? { videoTrack: latestVideo } : {})
      }
    }

    if (videoTrackState.status !== 'ready') {
      setVideoTrackState({ status: 'loading', track: null })
      try {
        const videoTrack = await createCameraTrack()
        if (unmountedRef.current) return
        latestVideo = videoTrack
        syncMediaStore()
        setVideoTrackState({ status: 'ready', track: videoTrack })
      } catch (videoError) {
        console.error('[TracksConfigure] Camera retry failed:', videoError)
        if (unmountedRef.current) return
        setVideoTrackState({
          status: 'failed',
          track: null,
          error: classifyError(videoError, 'video')
        })
      }
    }

    if (audioTrackState.status !== 'ready') {
      try {
        const audioTrack = await createMicrophoneTrack()
        if (unmountedRef.current) return
        latestAudio = audioTrack
        syncMediaStore()
        setAudioTrackState({ status: 'ready', track: audioTrack })
        setReady(true)
      } catch (audioError) {
        console.error('[TracksConfigure] Microphone retry failed:', audioError)
        if (unmountedRef.current) return
        setAudioTrackState({
          status: 'failed',
          track: null,
          error: classifyError(audioError, 'audio')
        })
      }
    }
  }

  useEffect(() => {
    const initializeTracks = async () => {
      if (tracks !== null) {
        // Both tracks successfully created
        setAudioTrackState({
          status: 'ready',
          track: tracks[0]
        })
        setVideoTrackState({
          status: 'ready',
          track: tracks[1]
        })
        mediaStore.current[0] = {
          audioTrack: tracks[0],
          videoTrack: tracks[1]
        }
        setReady(true)
      } else if (error) {
        console.error(
          '[TracksConfigure] Camera + microphone initialization failed:',
          error
        )

        // Creating both devices in one call fails as a whole when EITHER device fails, so create
        // them one at a time: a camera problem must not cost the call its audio, and a microphone
        // hiccup must not silently drop the video (which is what left the other side looking at
        // a placeholder icon before).
        let audioTrack: ILocalAudioTrack | null = null
        let audioError: unknown = null
        try {
          audioTrack = await createMicrophoneTrack()
        } catch (e) {
          audioError = e
          console.error(
            '[TracksConfigure] Microphone initialization failed:',
            e
          )
        }

        let videoTrack: ILocalVideoTrack | null = null
        let videoError: unknown = error
        try {
          videoTrack = await createCameraTrack()
        } catch (e) {
          videoError = e
          console.error('[TracksConfigure] Camera initialization failed:', e)
        }

        if (unmountedRef.current) return

        setVideoTrackState(
          videoTrack
            ? { status: 'ready', track: videoTrack }
            : {
                status: 'failed',
                track: null,
                error: classifyError(videoError, 'video')
              }
        )

        if (audioTrack) {
          setAudioTrackState({ status: 'ready', track: audioTrack })
          mediaStore.current[0] = {
            audioTrack,
            ...(videoTrack ? { videoTrack } : {})
          }
          setReady(true) // Join with whatever is available (audio is the minimum)
        } else {
          // A microphone is required to join (see ERROR_MESSAGES.microphone_*).
          setAudioTrackState({
            status: 'failed',
            track: null,
            error: classifyError(audioError, 'audio')
          })
          setReady(false)
        }
      }
    }

    initializeTracks()

    return () => {
      if (tracks) {
        // eslint-disable-next-line no-unused-expressions
        tracks[0]?.close()
        // eslint-disable-next-line no-unused-expressions
        tracks[1]?.close()
      }
    }
  }, [trackReady, error])

  // Close any separately created tracks when the call UI goes away.
  useEffect(() => {
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      separateTracksRef.current.forEach((track) => {
        try {
          track.close()
        } catch (e) {
          console.warn('[TracksConfigure] Failed to close track:', e)
        }
      })
      separateTracksRef.current = []
    }
  }, [])

  return (
    <TracksProvider
      value={{
        localVideoTrack: videoTrackState.track as ILocalVideoTrack | null,
        localAudioTrack: audioTrackState.track as ILocalAudioTrack | null,
        videoTrackState: {
          isAvailable: videoTrackState.status === 'ready',
          isLoading: videoTrackState.status === 'loading',
          error: videoTrackState.error
            ? {
                type: videoTrackState.error.type as
                  | 'permission_denied'
                  | 'device_unavailable'
                  | 'initialization_failed'
                  | 'unknown',
                message: videoTrackState.error.message
              }
            : undefined
        },
        audioTrackState: {
          isAvailable: audioTrackState.status === 'ready',
          isLoading: audioTrackState.status === 'loading',
          error: audioTrackState.error
            ? {
                type: audioTrackState.error.type as
                  | 'permission_denied'
                  | 'device_unavailable'
                  | 'initialization_failed'
                  | 'unknown',
                message: audioTrackState.error.message
              }
            : undefined
        },
        isAudioOnly:
          audioTrackState.status === 'ready' &&
          videoTrackState.status !== 'ready',
        retryTrackInitialization
      }}
    >
      {ready ? props.children : null}
    </TracksProvider>
  )
}

export default TracksConfigure
